import { Shell } from 'repluggable';
import { default as api } from './apis/api';
import {
  CompRef,
  PageRef,
  DocumentServicesObject,
  RouterRef,
  AppData as DmAppData,
  BuilderComponent,
} from '@wix/document-services-types';
import { DocumentServicesAPI } from '@wix/editorx-core-api';
import {
  PluginInstallationParams,
  PluginActionContext,
  WidgetPointer,
  ToolsEntryEventListener,
  InitAppOptions,
  InstallCallbacks,
  InstallOptions,
  SilentInstallAppsOptions,
  IntentInstallOptions,
  SilentInstallAppsConfig,
  SilentInstallAppsCallbacks,
  HostEditActionsDictionary,
  ExecutionMethodsDictionary,
  PlatformApiMethods,
  AppInstallOrigin,
  IPublicManifestAPI,
  UniversalSlotPointer,
  HostEditActionDefinition,
  EditActionBase,
  ActionNames,
} from '@wix/editor-platform-host-integration-apis';
import { IEditorPlatformPackagesBridge } from '@wix/editor-platform-interfaces';
import {
  AppData,
  ComponentRef,
  PageVariant,
  Rule,
} from '@wix/editor-platform-sdk-types';
import { initV2 } from './apis/bootstrap/initV2';
import {
  createWorkspaceApi,
  IWorkspaceApiAdapter,
} from './apis/utils/workspaceApiAdapter';
import {
  createExperimentsApi,
  IExperimentsApiAdapter,
} from './apis/utils/experimentsApiAdapter';
import { platformEvents } from './platformEvents';
import { reportBi } from './essentials';
import { ExtendedPlatformContext, PlatformContext } from './types/platformApi';
import { installApps } from './apis/services/unifiedComponents/installation/addApps';
import {
  initIntentInstallServices,
  getIntentInstallServices,
  createIntentInstallAsyncHandlers,
  notifyIntentInstallDidntHappen,
} from './apis/services/intentInstallService';
import { initEditActionsServices } from './apis/services/editActionsServices';
import { initPluginsServices } from './apis/services/plugins/init';
import { DocumentOverridesAPIKey } from '@wix/editor-platform-feature-store';
import {
  getAppRefreshServices,
  initAppRefreshService,
} from './apis/services/SdkEventHandlerService';
import {
  ComponentsPlatformService,
  BackwardCompatibleComponentsPlatformService,
} from '@wix/editor-platform-manifest-services';
import {
  createEditorPlatformHostIntegrationAPIError,
  EditorPlatformHostIntegrationAPIErrorCode,
} from './editorPlatformHostIntegrationAPIErrors';
import { SlotPointer } from './apis/services/plugins/slotPointer';
import { createAppNavigationService } from './apis/services/appNavigationService';
import { PlatformAppDependenciesService } from './apis/services/platformAppDependenciesService/platformAppDependenciesService';
import type { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';

import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

// We add here the contributeEditorPlatformPackagesBridge function to avoid circular dependency
declare module '@wix/editor-platform-host-integration-apis' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface EditorPlatformHostIntegrationAPI {
    contributeEditorPlatformPackagesBridge: (
      editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge>,
    ) => void;
  }
}

// these variables are being initiated in the "init" function. init function is called only once, but createEditorPlatformHostIntegrationAPI
// can be called multiple times, so need to save and share them
let context: PlatformContext;
const editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge> = {};
let componentsPlatformService: ComponentsPlatformService;
let backwardCompatibleComponentsPlatformService: BackwardCompatibleComponentsPlatformService;
let intentInstallServices: ReturnType<typeof initIntentInstallServices>;
let editActionsServices: ReturnType<typeof initEditActionsServices>;
let pluginsServices: ReturnType<typeof initPluginsServices>;
let appRefreshService: ReturnType<typeof initAppRefreshService>;

function createManifestAPI(
  compService?: BackwardCompatibleComponentsPlatformService,
): IPublicManifestAPI {
  if (!compService) {
    console.warn(
      createEditorPlatformHostIntegrationAPIError(
        EditorPlatformHostIntegrationAPIErrorCode.editorPlatformHostIntegrationUninitialized,
        'Editor Platform Host Integration API is not initialized. Returning mock manifest API.',
      ).withAPIName('manifest'),
    );
    return BackwardCompatibleComponentsPlatformService.getPact().getPublicManifestAPI();
  }

  return compService.getPublicManifestAPI();
}

function setPlatformE2EAPIs<T extends Object>(data: T) {
  if (!window.location.search.includes('&isqa=')) {
    return;
  }

  const platformE2EAPis = (window as any).__platform_e2e_api;

  (window as any).__platform_e2e_api = {
    ...platformE2EAPis,
    ...data,
  };
}

// new API is created for each host (can be multiple)
export function createEditorPlatformHostIntegrationAPI(
  shell: Shell,
): EditorPlatformHostIntegrationAPI {
  const documentServicesAPI = shell.getAPI(
    DocumentServicesAPI,
  ) as unknown as DocumentServicesObject;
  const workspaceAPI: IWorkspaceApiAdapter = createWorkspaceApi(shell);
  const experimentsAPI: IExperimentsApiAdapter = createExperimentsApi(shell);

  // needed for cases when intent install and isRunning function can be called before platform initialization
  let intentInstallServiceAsyncHandlers = createIntentInstallAsyncHandlers();

  let initialized = false;

  const editorPlatformHostIntegrationAPI: EditorPlatformHostIntegrationAPI = {
    init: (
      platformApiMethods: PlatformApiMethods,
      initAppOptions?: InitAppOptions,
      extraNamespaces?: string[],
    ) => {
      const {
        platformApiMethods: platformAPIMethods,
        componentsPlatformService: _componentsPlatformService,
        backwardCompatibleComponentsPlatformService:
          _backwardCompatibleComponentsPlatformService,
        intentInstallServices: _intentInstallServices,
        editActionsServices: _editActionsServices,
        pluginsServices: _pluginsServices,
        appRefreshService: _appRefreshService,
      } = api.init(
        {
          workspaceAPI,
          documentServicesAPI,
          experimentsAPI,
          editorPlatformPackagesBridge,
        },
        platformApiMethods,
        editorPlatformPackagesBridge.EditorPointerAPI ??
          ({} as EditorPointerServiceAPI),
        initAppOptions,
        extraNamespaces,
      );

      context = {
        platformApiMethods: platformAPIMethods,
        componentsPlatformService: _componentsPlatformService,
        intentInstallServices: _intentInstallServices,
        experimentsAPI,
        editorPlatformPackagesBridge,
        manifestAPI: createManifestAPI(
          _backwardCompatibleComponentsPlatformService,
        ),
      };

      PlatformAppDependenciesService.init(documentServicesAPI, context);

      componentsPlatformService = _componentsPlatformService;
      backwardCompatibleComponentsPlatformService =
        _backwardCompatibleComponentsPlatformService;
      intentInstallServices = _intentInstallServices;
      editActionsServices = _editActionsServices;
      pluginsServices = _pluginsServices;
      appRefreshService = _appRefreshService;

      setPlatformE2EAPIs({
        componentsPlatformService,
        platformAPIMethods,
        editorPointerAPI: editorPlatformPackagesBridge.EditorPointerAPI ?? {},
      });

      // TEMPORARY
      // TODO - EP-6022 remove after we finish integrating the new types with the editors.
      if (!initialized) {
        initialized = true;
        shell.contributeAPI(
          DocumentOverridesAPIKey,
          () => platformAPIMethods.document,
        );
      }
    },
    initV2,
    getWorkerManager: () => api.getPlatformInfraAPI(),
    getPlatformInfraAPI: () => api.getPlatformInfraAPI(),
    setEditorAPI: (namespace: string, platformAPI: PlatformApiMethods) =>
      api.setEditorAPI(documentServicesAPI, namespace, platformAPI),
    applications: {
      setAppPublicAPI: (apiName: string) =>
        api.applications.setAppPublicAPI(documentServicesAPI, apiName),
      createDependenciesDriver: (
        appDefinitionId: string,
        appVersion?: string,
      ) =>
        api.applications.createDependenciesDriver(
          documentServicesAPI,
          appDefinitionId,
          appVersion,
        ),
      unifiedComponents: {
        installApps: (
          appDefinitionIds: string[],
          installOptions: InstallOptions,
          callbacks?: InstallCallbacks,
        ) =>
          installApps(
            context,
            documentServicesAPI,
            appDefinitionIds,
            installOptions,
            callbacks,
          ),
      },
      install: (
        appDefinitionIds: string[],
        installOptions: InstallOptions,
        callbacks?: InstallCallbacks,
      ) =>
        api.applications.install(
          context,
          documentServicesAPI,
          appDefinitionIds,
          installOptions,
          callbacks,
        ),
      fetchNames: (appDefIds: string[], languageCode?: string) =>
        api.applications.fetchNames(appDefIds, languageCode),
      getAppsMarketListing: (params) =>
        api.applications.getAppsMarketListing(params),
      silentInstall: (
        appsToInstallSilently: string[],
        options: SilentInstallAppsOptions,
        serviceConfig: SilentInstallAppsConfig,
        serviceCallbacks: Partial<SilentInstallAppsCallbacks>,
      ) =>
        api.applications.silentInstall(
          context,
          documentServicesAPI,
          experimentsAPI,
          appsToInstallSilently,
          options,
          serviceConfig,
          serviceCallbacks,
        ),
      intentInstall: async (options: IntentInstallOptions) => {
        // in studio intent install call doesn't know anything about platform initialization
        // so we want to make sure that intent install services are initialized before calling it

        intentInstallServiceAsyncHandlers = createIntentInstallAsyncHandlers();

        intentInstallServiceAsyncHandlers.setIsIntentInstallStarting(true);
        const awaitedIntentInstallServices = await getIntentInstallServices();

        const intentInstallResult = awaitedIntentInstallServices.intentInstall(
          context,
          options,
        );

        intentInstallServiceAsyncHandlers.intentInstallLaunched();
        intentInstallServiceAsyncHandlers.setIsIntentInstallStarting(false);

        return intentInstallResult;
      },

      getIsIntentInstallRunningAsync: async () => {
        if (intentInstallServiceAsyncHandlers.getIsIntentInstallStarting()) {
          await intentInstallServiceAsyncHandlers.waitForIntentInstallToStart();
        }

        return intentInstallServices?.isIntentInstallRunning() || false;
      },

      isIntentInstallRunning: () =>
        intentInstallServices?.isIntentInstallRunning() || false,
      isSilentInstallRunning: () =>
        api.applications.isSilentInstallRunning() ||
        intentInstallServices?.isIntentInstallRunningInSilentMode() ||
        false,
      notifyIntentInstallDidntHappen,
      isAppUpdateAvailable: (appDefId: string, version?: string) =>
        api.applications.isAppUpdateAvailable(
          documentServicesAPI,
          appDefId,
          version,
        ),
      addAppToRefresh: async (appsDefinitionIds: string[]) => {
        const awaitedAppRefreshService = await getAppRefreshServices();
        awaitedAppRefreshService.addAppToRefresh(appsDefinitionIds);
      },
      refreshVisitedApps: async () => {
        const awaitedAppRefreshService = await getAppRefreshServices();
        awaitedAppRefreshService.refreshVisitedApps();
      },
      openApp: async (appDefId: string) => {
        const service = createAppNavigationService(
          context,
          documentServicesAPI,
        );
        return service.openApp(appDefId);
      },
    },
    pages: {
      isPageEssential: (pageId: string) =>
        api.pages.isPageEssential(documentServicesAPI, experimentsAPI, pageId),
      isPageDuplicatable: (pageId: string) =>
        api.pages.isPageDuplicatable(
          documentServicesAPI,
          experimentsAPI,
          pageId,
        ),
    },
    components: {
      builder: {
        add: (builderComponent: BuilderComponent, containerRef: CompRef) => {
          const extendedContext: ExtendedPlatformContext = {
            ...context,
            documentServices: documentServicesAPI,
            manifestAPI: createManifestAPI(
              backwardCompatibleComponentsPlatformService,
            ),
          };
          return api.components.builder.add(
            extendedContext,
            builderComponent,
            containerRef,
          );
        },
        addByType: async (
          componentType: string,
          containerPointer: EditorPointer<CompRef>,
          options?: {
            preset?: string;
          },
        ) => {
          const extendedContext: ExtendedPlatformContext = {
            ...context,
            documentServices: documentServicesAPI,
            manifestAPI: createManifestAPI(
              backwardCompatibleComponentsPlatformService,
            ),
          };
          return api.components.builder.addByType(
            extendedContext,
            componentType,
            containerPointer,
            options,
          );
        },
      },
      getComponentApp: (compRef: CompRef) =>
        api.components.getComponentApp(documentServicesAPI, compRef),
      handleEssentialsComponentsBeforeDelete: (compRefs: CompRef[]) =>
        api.components.handleEssentialsComponentsBeforeDelete(
          context,
          documentServicesAPI,
          compRefs,
        ),
      getPlatformizedComponentData: (componentRef: CompRef) =>
        api.components.getPlatformizedComponentData(
          documentServicesAPI,
          componentRef,
        ),
      isWidgetStretchable: (compRef: CompRef, appData?: DmAppData) =>
        api.components.isWidgetStretchable(
          documentServicesAPI,
          compRef,
          appData,
        ),
      isWidgetFullWidth: (compRef: CompRef, appData?: DmAppData) =>
        api.components.isWidgetFullWidth(documentServicesAPI, compRef, appData),
      getComponentIdentifiers: (compRef: CompRef, appData?: DmAppData) =>
        api.components.getComponentIdentifiers(
          documentServicesAPI,
          compRef,
          appData,
        ),
      isComponentEssential: (compRef: CompRef) =>
        api.components.isComponentEssentialByRef(
          documentServicesAPI,
          experimentsAPI,
          compRef,
        ),
      bindings: () => componentsPlatformService.bindings,
      capabilities: () => componentsPlatformService.capabilities,
    },
    sdk: {
      overrideImplementation: api.sdk.overrideImplementation,
    },
    pageReplace: {
      setReplacerPage: (
        pageRef: PageRef,
        replacerPageRef: PageRef,
        setAsActive: boolean,
      ) =>
        api.pageReplace.setReplacerPage(documentServicesAPI, {
          replacerPageRef,
          pageRef,
          setAsActive,
        }),
      openPageVariantsPanel: (
        pageTitle: string,
        variants: PageVariant[],
        pageManagingAppDefId: string,
        pageRef: PageRef,
      ) =>
        api.pageReplace.openPageVariantsPanel(documentServicesAPI, context, {
          pageTitle,
          variants,
          pageManagingAppDefId,
          pageRef,
        }),
      addVariantToPage: (
        pageRef: PageRef,
        variantPageRef: PageRef,
        rule: Rule,
        variantId: string,
      ) =>
        api.pageReplace.addVariantToPage(documentServicesAPI, {
          pageRef,
          variantPageRef,
          rule,
          variantId,
        }),
      getPageInfo: (pageRef: PageRef) =>
        api.pageReplace.getPageInfo(documentServicesAPI, pageRef),
      getPageVariations: (pageRef: PageRef) =>
        api.pageReplace.getPageVariations(documentServicesAPI, pageRef),
      isReplaceable: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.isReplaceable(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
      getPageAvailableVariants: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.getPageAvailableVariants(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
      getOriginalPageRef: (pageRef: PageRef) =>
        api.pageReplace.getOriginalPageRef(documentServicesAPI, pageRef),
      isReplacer: (pageRef: PageRef) =>
        api.pageReplace.isReplacer(documentServicesAPI, pageRef),
      isVariant: (pageRef: PageRef) =>
        api.pageReplace.isVariant(documentServicesAPI, pageRef),
      isReplacerActive: (pageRef: PageRef) =>
        api.pageReplace.isReplacerActive(documentServicesAPI, pageRef),
      isRouterExistForPage: (pageRef: PageRef) =>
        api.pageReplace.isRouterExistForPage(documentServicesAPI, pageRef),
      createRouter: (pageRef: PageRef) =>
        api.pageReplace.createRouter(documentServicesAPI, pageRef),
      connectPageToRouter: (routerRef: RouterRef, pageRef: PageRef) =>
        api.pageReplace.connectPageToRouter(
          documentServicesAPI,
          routerRef,
          pageRef,
        ),
      createBlankReplacerPage: (pageRef: PageRef) =>
        api.pageReplace.createBlankReplacerPage(
          context,
          documentServicesAPI,
          pageRef,
        ),
      openDeleteReplacerPageModal: (pageRef: PageRef) =>
        api.pageReplace.openDeleteReplacerPageModal(
          context,
          documentServicesAPI,
          pageRef,
        ),
      getPremiumFeatureName: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.getPremiumFeatureName(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
    },
    viewState: {
      getViewState: (appData: AppData, componentRef: ComponentRef) => {
        return api.viewState.getViewState(
          documentServicesAPI,
          appData,
          componentRef,
        );
      },
      setViewState: (
        appData: AppData,
        componentRef: ComponentRef,
        viewState: string,
      ) => {
        api.viewState.setViewState(
          documentServicesAPI,
          appData,
          componentRef,
          viewState,
        );
      },
    },
    canvasOverlay: api.canvasOverlay,
    toolsEntry: {
      register: (listener: ToolsEntryEventListener) => {
        api.toolsEntry.toolsEntryStore.register(listener);
      },
      unregister: (listener: ToolsEntryEventListener) => {
        api.toolsEntry.toolsEntryStore.unregister(listener);
      },
    },
    platformEvents,
    slots: {
      autoInstall: (
        pluginInstallationParams: PluginInstallationParams,
        pluginActionContext: PluginActionContext,
      ) =>
        api.slots.autoInstall.install(
          context,
          documentServicesAPI,
          createManifestAPI(backwardCompatibleComponentsPlatformService),
          pluginInstallationParams,
          pluginActionContext,
          experimentsAPI,
        ),
      autoInstallWithNavigation: (
        pluginInstallationParams: PluginInstallationParams,
        pluginActionContext: PluginActionContext,
      ) => {
        return api.slots.autoInstall.installWithNavigation(
          context,
          documentServicesAPI,
          createManifestAPI(backwardCompatibleComponentsPlatformService),
          pluginInstallationParams,
          pluginActionContext,
          experimentsAPI,
        );
      },
      getSlotPlaceholderRefByContentRef: (compRef: CompRef) =>
        pluginsServices.slots.getSlotPlaceholderRefByContentRef(compRef),
      getUniversalSlotPointerByPluginCompRef: (compRef: CompRef) =>
        pluginsServices.slots.getUniversalSlotPointerByPluginCompRef(compRef),
      getWidgetHostRefBySlotRef: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetHostRefBySlotRef(compRef),
      getWidgetHostRefByWidgetRef: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetHostRefByWidgetRef(compRef),
      getWidgetSlot: (
        slot: CompRef | UniversalSlotPointer,
        options: { includePrivate: boolean },
      ) =>
        pluginsServices.slots.getWidgetSlot(
          SlotPointer.create(
            documentServicesAPI,
            createManifestAPI(backwardCompatibleComponentsPlatformService),
            experimentsAPI,
            slot,
          ),
          options,
        ),
      getWidgetSlots: (
        widgetCompRef: CompRef,
        options: { includePrivate: boolean },
      ) => pluginsServices.slots.getWidgetSlots(widgetCompRef, options),
      getSlotPosition: (compRef: CompRef) =>
        pluginsServices.slots.getSlotPosition(compRef),
      getCompRefForSlotSelection: (compRef: CompRef) =>
        pluginsServices.slots.getCompRefForSlotSelection(
          new SlotPointer({ slotRef: compRef }),
        ),
      getSlotInBlocksAppEditor: (slotCompRef: CompRef) =>
        pluginsServices.slots.getSlotInBlocksAppEditor(slotCompRef),
      getWidgetSlotsToMoveContent: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetSlotsToMoveContent(
          new SlotPointer({ slotRef: compRef }),
        ),
      getNearestWidgetHostRef: (compRef: CompRef) =>
        pluginsServices.slots.getNearestWidgetHostRef(compRef),
      hasWidgetSlot: (widgetCompRef: CompRef) =>
        pluginsServices.slots.hasWidgetSlot(widgetCompRef),
      isSlotPublic: (slot: CompRef | UniversalSlotPointer) =>
        pluginsServices.slots.isSlotPublic(
          SlotPointer.create(
            documentServicesAPI,
            createManifestAPI(backwardCompatibleComponentsPlatformService),
            experimentsAPI,
            slot,
          ),
        ),
      moveToSlot: (
        fromSlotComponentRef: CompRef,
        toSlotComponentRef: CompRef,
        pluginActionContext: PluginActionContext,
      ) =>
        pluginsServices.slots.moveToSlot(
          context,
          new SlotPointer({ slotRef: fromSlotComponentRef }),
          new SlotPointer({ slotRef: toSlotComponentRef }),
          pluginActionContext,
        ),
      highlightSlot: (compRef: CompRef) =>
        pluginsServices.slots.highlightSlot(
          new SlotPointer({ slotRef: compRef }),
        ),
      unhighlightSlot: (compRef: CompRef) =>
        pluginsServices.slots.unhighlightSlot(
          new SlotPointer({ slotRef: compRef }),
        ),
      isSlotContentPotentialMovable: (slot: CompRef | UniversalSlotPointer) =>
        pluginsServices.slots.isSlotContentPotentialMovable(
          SlotPointer.create(
            documentServicesAPI,
            createManifestAPI(backwardCompatibleComponentsPlatformService),
            experimentsAPI,
            slot,
          ),
        ),
      headerPlugins: {
        isHeaderPlugin: (compRef: CompRef) =>
          pluginsServices.headerPlugins.isHeaderPlugin(compRef),
      },
      plugins: {
        isPluginComponent: (widgetPluginCompRef: CompRef) =>
          pluginsServices.plugins.isPluginComponent(widgetPluginCompRef),
        showOnPage: (compRef: CompRef) =>
          pluginsServices.plugins.showOnPage(
            new SlotPointer({ slotRef: compRef }),
          ),
        install: (
          slot: ComponentRef | { hostCompRef: CompRef; slotId: string },
          widgetPointer: WidgetPointer,
          pluginActionContext: PluginActionContext,
        ) =>
          pluginsServices.plugins.installWidgetPlugin(
            context,
            SlotPointer.create(
              documentServicesAPI,
              createManifestAPI(backwardCompatibleComponentsPlatformService),
              experimentsAPI,
              slot,
            ),
            widgetPointer,
            pluginActionContext,
          ),
        uninstall: (
          slot: ComponentRef | { hostCompRef: CompRef; slotId: string },
          pluginActionContext: PluginActionContext,
        ) =>
          pluginsServices.plugins.uninstallWidgetPlugin(
            context,
            SlotPointer.create(
              documentServicesAPI,
              createManifestAPI(backwardCompatibleComponentsPlatformService),
              experimentsAPI,
              slot,
            ),
            pluginActionContext,
          ),
      },
    },

    get manifest() {
      return createManifestAPI(backwardCompatibleComponentsPlatformService);
    },
    get hierarchy() {
      if (!componentsPlatformService) {
        console.warn(
          createEditorPlatformHostIntegrationAPIError(
            EditorPlatformHostIntegrationAPIErrorCode.editorPlatformHostIntegrationUninitialized,
            'Editor Platform Host Integration API is not initialized. Returning mock hierarchy API.',
          ).withAPIName('hierarchy'),
        );
        return BackwardCompatibleComponentsPlatformService.getPact().getPublicHierarchyAPI();
      }
      return backwardCompatibleComponentsPlatformService.getPublicHierarchyAPI();
    },
    panels: {
      addElements: {
        openIfExist: (compRef: CompRef) =>
          api.panels.addElements.openIfExist(documentServicesAPI, compRef),
      },
      upgradeSiteConfirmation: {
        open: (appDefinitionId: string, premiumFeatureName: string) =>
          api.panels.upgradeSiteConfirmation.open(
            documentServicesAPI,
            context,
            appDefinitionId,
            premiumFeatureName,
          ),
      },
      addPanel: {
        getGroups: () =>
          api.panels.addPanel.getGroups(documentServicesAPI, context),
        getAppsComponents: () =>
          api.panels.addPanel.getAppsComponents(documentServicesAPI, context),
        getEditorExposureAppComponents: () =>
          api.panels.addPanel.getEditorExposureAppComponents(
            documentServicesAPI,
            context,
          ),
      },
    },
    editActions: {
      getActions: (editorPointers, metadata) => {
        const pointers = [editorPointers].flat();
        const result = editActionsServices.generateEditActions(
          pointers,
          metadata,
        );
        return {
          nativeActions: result.nativeActions as HostEditActionDefinition[],
          customActions: result.customActions,
          systemActions: result.systemActions as EditActionBase<ActionNames>[],
        };
      },
      contributeHostActions: (
        hostActions: Partial<HostEditActionsDictionary>,
      ) => api.editActions.contributeHostActions(hostActions),
      overrideContributedActionUI: (actionName, actionUI) =>
        api.editActions.overrideActionUI(actionName, actionUI),
      contributeExecutionMethods: (
        executionMethods: ExecutionMethodsDictionary,
      ) => api.editActions.contributeExecutionMethods(executionMethods),
    },
    reportBi,
    addons: {
      install: (
        appDefinitionId: string,
        options: { origin: AppInstallOrigin },
      ) =>
        api.applications.installWithConsentModal(
          context,
          documentServicesAPI,
          appDefinitionId,
          {
            appsOptions: {
              [appDefinitionId]: {
                origin: options.origin,
                headlessInstallation: true,
              },
            },
          },
        ),
    },
    contributeEditorPlatformPackagesBridge: <
      Key extends keyof IEditorPlatformPackagesBridge,
    >(
      editorPlatformPackagesBridgeInput: Partial<IEditorPlatformPackagesBridge>,
    ) => {
      for (const item of Object.entries(editorPlatformPackagesBridgeInput)) {
        const [apiName, apiImpl] = item as [
          Key,
          IEditorPlatformPackagesBridge[Key],
        ];

        editorPlatformPackagesBridge[apiName] = apiImpl;
      }
    },
  };

  setPlatformE2EAPIs({
    documentServicesAPI,
    editorPlatformHostIntegrationAPI,
  });

  return editorPlatformHostIntegrationAPI;
}
